import { CommandType } from '@open-template-hub/animated-code-editor';
import { environment } from '../../../environments/environment';
import { environmentCommon } from '../../../environments/environment-common';
import { CompanyLine, ProductLine } from '../../model/product/product.model';
import { URLS } from '../navigation/navigation.data';
import { PRODUCT_RIBBONS } from '../ribbon/ribbon.data';
import { TECH_STACK } from '../tech-stack/tech-stack.data';
import { ThemeColorSettings } from '../theme/theme.data';

const OPEN_SOURCE_FEATURE = {
  name: $localize`:@@productData.openSource.name:Open Source`,
  description: $localize`:@@productData.openSource.description:No need to pay a price to get started. Get the latest updates from community driven open source project.`,
};

const PROFESSIONAL_HELP_FEATURE = {
  name: $localize`:@@productData.professionalHelp.name:Professional Help`,
  description: $localize`:@@productData.openSource.description:Get an up and running server.`,
};

const CUSTOMISED_SOLUTIONS_FEATURE = {
  name: $localize`:@@productData.customisedSolutions.name:Customised Solutions`,
  description: $localize`:@@productData.customisedSolutions.description:Fully customised functional solutions for your needs.`,
};

const CI_CD_AUTOMATION_FEATURE = {
  name: $localize`:@@productData.ciCdAutomation.name:CI/CD Automation`,
  description: $localize`:@@productData.ciCdAutomation.description:Get a CI/CD integration with automated API tests.`,
};

const CUSTOMISED_AUTOMATIONS_FEATURE = {
  name: $localize`:@@productData.customisedAutomations.name:Customised Automations`,
  description: $localize`:@@productData.customisedAutomations.description:Fully customised automation solutions for your needs.`,
};

const SECURITY_REPORTS_FEATURE = {
  name: $localize`:@@productData.customisedAutomationsSecurity.name:Reports`,
  description: $localize`:@@productData.customisedAutomationsSecurity.description:Get a security report at the end of each month.`,
};

const CUSTOM_SECURITY_COMPLIANCE_FEATURE = {
  name: $localize`:@@productData.customSecurityCompliance.name:Custom Security Compliance`,
  description: $localize`:@@productData.customSecurityCompliance.description:Fully customised security compliance solutions for your needs.`,
};

const DEFAULT_OPEN_SOURCE_SERVER_FEATURES = [
  {
    name: $localize`:@@productData.defaultOpenSourceServerFeatures.1:Core Features`,
    details: [
      OPEN_SOURCE_FEATURE,
      PROFESSIONAL_HELP_FEATURE,
      CUSTOMISED_SOLUTIONS_FEATURE,
    ],
  },
  {
    name: $localize`:@@productData.defaultOpenSourceServerFeatures.2:Quality and DX`,
    details: [
      CI_CD_AUTOMATION_FEATURE,
      CUSTOMISED_AUTOMATIONS_FEATURE,
    ],
  },
  {
    name: $localize`:@@productData.defaultOpenSourceServerFeatures.3:Security`,
    details: [
      SECURITY_REPORTS_FEATURE,
      CUSTOM_SECURITY_COMPLIANCE_FEATURE,
    ],
  },
];

const FREE_PRICING_OPTION = {
  title: $localize`:@@productData.freePricingOption.title:Free`,
  description: $localize`:@@productData.freePricingOption.description:For Individuals`,
  price: {
    currency: '$',
    value: '0',
    subscriptionTime: $localize`:@@productData.freePricingOption.price.subscriptionTime:mo.`,
  },
  features: [['false', '', ''], ['', ''], ['', '']]
};

const STANDARD_PRICING_OPTION = {
  title: $localize`:@@productData.standardPricingOption.title:Standard`,
  description: $localize`:@@productData.standardPricingOption.description:For Startups`,
  ribbon: PRODUCT_RIBBONS.get('deal'),
  contactRequired: false,
  features: [['false', 'false', ''], ['false', ''], [$localize`:@@productData.standardPricingOption.features.1:Up to 10 users`, '']]
};

const ENTERPRISE_PRICING_OPTION = {
  title: $localize`:@@productData.enterprisePricingOption.title:Enterprise`,
  description: $localize`:@@productData.enterprisePricingOption.description:For Enterprises`,
  contactRequired: false,
  features: [['false', 'false', 'false'], ['false', 'false'], [$localize`:@@productData.enterprisePricingOption.features.1:Unlimited users`, 'false']]
};

const DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS = [
  FREE_PRICING_OPTION,
  STANDARD_PRICING_OPTION,
  ENTERPRISE_PRICING_OPTION,
];

export const PRODUCT_LINES: ProductLine[] = [
  {
    key: 'product',
    name: '',
    description: "Please go through our companies",
    products: [
      {
        key: 'castrol-product',
        name: 'Castrol',
        metadata: {
          title: "Castrol Industrial Lubricants – Performance & Trust",
          description: "Castrol’s high-performance industrial lubricants, distributed by SR Engineers, ensure superior efficiency & reliability in India",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.authServerTemplate.description:Castrol, a global pioneer in lubricating solutions, has been at the cutting edge of
innovation for over a century. Castrol's high-performance lubricants serve the
automotive, aerospace, manufacturing, and energy sectors. Castrol supplies cutting-
edge lubrication technology that enhances sustainability, performance, and
efficiency.Castrol manufactures neat and soluble cutting oils, rust and corrosion
preventives, mineral and FRHF hydraulic oils, engine oils, gear oils, transformer oils,
synthetic oils (poly glycol, poly alfa olefines), speciality greases for heavy duty and
high temperatures, gel-based girth gear lubricants and other speciality lubricant
products.`,
        longDescription: `
        <p>Castrol is one of our most enduring partnerships. The strength of this association is derived from the mutual trust and our shared dedication to providing quality service to our customers.</p>
<p>Castrol, a global leader in lubrication solutions, has been at the forefront of innovation for more than a century. The automotive, aerospace, manufacturing, and energy sectors are all served by Castrol&apos;s high-performance lubricants. Castrol has been a dependable business partner in India, providing businesses with state-of-the-art lubrication technology that improves sustainability, performance, and efficiency.</p>
<p>Over the course of more than a century, Castrol, a global pioneer in the manufacture of lubricating solutions, has been at the forefront of innovation. High-performance lubricants manufactured by Castrol are utilised in the automotive, aerospace, manufacturing, and energy areas of the economy. Castrol provides cutting-edge lubrication technology that, in addition to improving performance and efficiency, also contributes to increased sustainability. Castrol is a manufacturer of a wide range of products, including but not limited to: neat and soluble cutting oils, rust and corrosion preventives, mineral and FRHF hydraulic oils, engine oils, gear oils, transformer oils, synthetic oils (poly glycol, poly alfa olefines), speciality greases for heavy duty and high temperatures, gel-based girth gear lubricants and other speciality lubricant products.</p>
<p><strong>Why choose Castrol Industrial Lubricants?</strong></p>
<p><strong>🔹</strong><strong>Proven Expertise</strong>&ndash; Backed by over 125 years of lubricant innovation and global leadership.</p>
<p>🔹<strong>Performance &amp; Efficiency</strong>&ndash; Enhances equipment life, reduces downtime, and lowers maintenance costs.</p>
<p>🔹<strong>Sustainability</strong>&ndash; Environmentally friendly formulations that align with modern industrial sustainability goals.</p>
<p>🔹<strong>Industry-Specific Solutions</strong>&ndash; Tailored lubricants designed for manufacturing, automotive, mining, energy, and heavy machinery industries.</p>
<p><br></p>
<p>Businesses in a wide variety of industries put their faith in Castrol India&apos;s industrial lubricants because of their dependability, performance, and capacity to enhance operational efficiency. By maintaining a strong emphasis on innovation, efficiency, and sustainability, Castrol continues to be the lubrication partner of choice for businesses that are looking to increase their productivity while simultaneously lowering their operational costs.</p>
<p>Molub-Alloy is Castrol&apos;s most powerful lubricant, designed to withstand the most severe loads on Earth. The complete line of Molub-Alloy greases, oils and pastes is liquid engineered to incorporate a variety of compounds that contain microscopic solids, which provide an additional layer of protection.<br>The Castrol Optigear line of advanced gear oils is designed with an advanced formula that optimises efficiency and reduces friction, thereby reducing levelized costs.<br>Lubricants are the cause of 60% of equipment malfunctions. Castrol Tribol range of products are designed to extend the lifespan of equipment by actively adapting to operating conditions.</p>
        `,
        url: 'https://www.castrol.com/en_in/india/home/industries.html',
        logo: 'assets/logo/Castrol.svg',
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.oauth,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.postgreSql,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'bahco-product',
        name: 'Bahco',
        metadata: {
          title: "Bacho Blades - Precision cutting blades & tools",
          description: "Explore Bahco professional hand tools blades & saws with SR Group, trusted distributor for industrial and engineering solutions.",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.paymentServerTemplate.description:Bahco designs and manufactures tools and saws for the most demanding
professionals, prioritising innovation, performance, and ergonomics. Bacho have
been inventors for over 150 years, introducing the most innovative tools in the world
and selling through distributors of which we are one and we mutually form long-term
partnerships focusing on customer relation and satisfaction. Products: Bahco band
saw, the Holesaw hacksaw, the Jig saw blade, the Hand saw blade, the Power Hack
Saw blade, the Hole Hacksaw, the Hole Saw set, and the Reciprocating Saw.`,
        url: 'https://www.bahco.com/int_en/products/bandsaws-power-saws/metal-cutting-carbide-bandsaws.html',
        logo: 'assets/logo/Bahco.svg',
        longDescription: `
        <p>Putting an emphasis on innovation, performance, and ergonomics, Bahco is a company that
        designs and manufactures saws and other tools for the most demanding professionals. Bacho
        has been in the business of inventing for more than 150 years, introducing the most cutting-
        edge tools in the world. They sell their products through distributors, an organisation of
        which we are a part, and we form long-term partnerships with them that are centred on the
        satisfaction and relationship of our customers<p>
        <p>Bahco is recognized for its commitment to innovation and quality, introducing industry-firsts
        like the pipe wrench and the modern adjustable wrench. The brand continues to focus on
        ergonomic designs to enhance user comfort and efficiency. Among the products available are
        the Bahco band saw, the Holesaw hacksaw, the Jig saw blade, the Hand saw blade, the Power
        Hack Saw blade, the Hole Hacksaw, the Hole Saw set, and the Reciprocating Saw<p>
        `,
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.payment,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.postgreSql,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'ewac-product',
        name: 'EWAC',
        metadata: {
          title: "EWAC Products – Welding, Maintenance and Repair",
          description: "Alcade Engineers offers EWAC products - premium alloys, electrodes and solutions for industrial maintenance and repair",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.fileStorageTemplate.description:Established in 1962 as a joint venture between Larsen &amp; Toubro Limited, India, and
Eutectic Castolin Group, Switzerland, EWAC Alloys Limited specialises in
maintenance and repair solutions within the welding industry. Their product lineup
includes specialised welding alloys, electrodes, gas brazing products, soldering
alloys, thermal spray powders, metal and flux-cored continuous wires, polymer-
based compounds, wear-resistant liners, ceramic products, and welding equipment.
The company serves sectors such as steel, cement, power, sugar, mining, oil and
gas refineries, tyre, port, and road transport industries.`,
        url: 'https://www.ewacalloys.com',
        logo: 'assets/logo/Ewac.svg',
        longDescription: `<p>
    EWAC Alloys Limited was established in 1962 as a joint venture between Larsen & Toubro Limited (India) and Eutectic Castolin Group (Switzerland). The company specializes in providing solutions for maintenance and repair within the welding industry.
  </p>

  <p>
    EWAC offers a comprehensive range of products and services for corrosion resistance, wear protection, and welding, catering to a wide range of industries including steel, cement, power, sugar, mining, oil and gas refineries, tyre, port, and road transport industries.
  </p>

  <p>
    Their product lineup includes:
    specialised welding alloys, electrodes, gas brazing products, soldering alloys, thermal spray powders, metal and flux-cored continuous wires, polymer-based compounds, wear-resistant liners, ceramic products, and welding equipment.
  </p>

  <h3>Product Categories</h3>

  <h4>1. Welding & Brazing Alloys</h4>
  <ul>
    <li>
      <strong>Specialized Welding Electrodes:</strong> For joining and hard-facing applications.
    </li>
    <li>
      <strong>Gas Brazing & Soldering Alloys:</strong> Suitable for precision metal joining.
    </li>
    <li>
      <strong>Metal & Flux-Cored Continuous Wires:</strong> High-performance welding wires for industrial use.
    </li>
  </ul>

  <h4>2. Thermal Spray & Wear Protection Solutions</h4>
  <ul>
    <li>
      <strong>Thermal Spray Powders:</strong> Used for coating and wear protection in harsh environments.
    </li>
    <li>
      <strong>Wear-Resistant Liners & Ceramic Products:</strong> Enhancing durability in high-wear industries.
    </li>
  </ul>

  <h4>3. Polymer-Based Compounds & Coatings</h4>
  <ul>
    <li>
      <strong>Metal Repair Compounds:</strong> Used for machinery and structural repairs.
    </li>
    <li>
      <strong>Corrosion & Abrasion-Resistant Coatings:</strong> Protecting metal surfaces from deterioration.
    </li>
  </ul>

  <h4>4. Welding Equipment & Accessories</h4>
  <ul>
    <li>
      <strong>Welding Consumables:</strong> Fluxes, rods, and welding accessories.
    </li>
    <li>
      <strong>Welding Machines & Tools:</strong> Equipment for industrial welding and maintenance applications.
    </li>
  </ul>

  <h4>5. Industry-Specific Solutions</h4>
  <ul>
    <li>
      <strong>Steel, Cement & Power Industries:</strong> Wear-resistant coatings and hard-facing solutions.
    </li>
    <li>
      <strong>Mining, Oil & Gas Refineries:</strong> Heavy-duty welding and anti-corrosion products.
    </li>
    <li>
      <strong>Automotive & Transportation:</strong> Maintenance and repair solutions for vehicle components.
    </li>
  </ul>`,
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.fileStorage,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.postgreSql,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'groz-product',
        name: 'Groz',
        metadata: {
          title: "GROZ– Tools, Maintenance & construction equipment",
          description: "Explore GROZ precision tools, lubrication and maintenance equipment with Alcade Engineers, trusted industrial distributor in Hyderabad",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.mailServerTemplate.description:Groz Engineering Tools is a leading global manufacturer specialising in lubrication
equipment, precision engineering, professional hand tools, and automotive shop
equipment. Groz serves industries such as automotive, industrial, agriculture,
mining, and DIY, delivering engineering solutions that emphasize precision and
durability. High quality products include Lubrication Equipment such as Grease guns,
oil pumps, and fluid handling solutions for industrial and automotive applications;
Precision Engineering Tools such as High-performance measuring instruments,
cutting tools, and workshop essentials; Hand Tools such as Wrenches, pliers,
hammers, and other professional-grade tools for various industries; Automotive &amp;

Industrial Equipment such as Shop presses, work lights, fuel transfer pumps, and
accessories for maintenance and repair.`,
        url: 'https://groz-tools.com',
        logo: 'assets/logo/GROZ.svg',
        longDescription: `
  <p>
    Groz Engineering Tools is a leading global manufacturer that specialises in lubrication equipment, precision engineering, professional hand tools, and automotive shop equipment. Renowned for delivering engineering solutions with a focus on precision and durability, Groz serves a wide range of industries, including automotive, industrial, agricultural, mining, manufacturing, and DIY.
  </p>
  <p>
    The Groz product line is both high-performance and diverse, offering:
  </p>

  <ul>
    <li><strong>Lubrication Equipment:</strong> Grease guns, oil pumps, and fluid handling solutions.</li>
    <li><strong>Precision Engineering Tools:</strong> Measuring instruments, cutting tools, and workshop essentials.</li>
    <li><strong>Hand Tools:</strong> Wrenches, pliers, hammers, and more for professional use.</li>
    <li><strong>Automotive & Industrial Equipment:</strong> Shop presses, work lights, fuel transfer pumps, and maintenance tools.</li>
  </ul>

  <h3>Primary Product Categories</h3>

  <h4>1. Lubrication Equipment</h4>
  <ul>
    <li><strong>Grease Guns & Accessories:</strong> Manual, pneumatic, and battery-operated options.</li>
    <li><strong>Oil & Fluid Handling Equipment:</strong> Oil pumps, fuel transfer pumps, fluid dispensers.</li>
    <li><strong>Grease & Oil Pumps:</strong> Heavy-duty pumps for high-viscosity fluids.</li>
  </ul>

  <h4>2. Hand Tools & Precision Tools</h4>
  <ul>
    <li><strong>Wrenches & Spanners:</strong> Open-end, ring spanners, and combination wrenches.</li>
    <li><strong>Hammers & Striking Tools:</strong> Claw hammers, sledgehammers, dead blow hammers.</li>
    <li><strong>Cutting & Measuring Tools:</strong> Precision squares, calipers, micrometers.</li>
  </ul>

  <h4>3. Work Lights & Inspection Lamps</h4>
  <ul>
    <li><strong>LED Work Lights:</strong> Rechargeable and corded lights for industrial applications.</li>
    <li><strong>Flashlights & Torches:</strong> High-intensity LED flashlights.</li>
  </ul>

  <h4>4. Automotive & Industrial Equipment</h4>
  <ul>
    <li><strong>Shop Equipment:</strong> Bench vices, jack stands, hydraulic shop presses.</li>
    <li><strong>Fuel Transfer Equipment:</strong> Pumps for diesel, petrol, and AdBlue with flow meters.</li>
    <li><strong>Air Accessories:</strong> Air blow guns, air hoses, tire inflators.</li>
  </ul>

  <h4>5. Magnetic & Clamping Tools</h4>
  <ul>
    <li><strong>Magnetic Pick-Up Tools:</strong> Magnetic lifters for workshop use.</li>
    <li><strong>Clamping Solutions:</strong> C-clamps, quick-release clamps, bench vices.</li>
  </ul>

  <h4>6. Fluid Measurement & Dispensing</h4>
  <ul>
    <li><strong>Flow Meters:</strong> Digital and mechanical meters for fuel and oil.</li>
    <li><strong>Drum & Barrel Equipment:</strong> Rotary drum pumps and siphon pumps.</li>
  </ul>

  <p>
    Groz products are known for their innovative designs, durable construction, and precise engineering—ideal for professionals and industries that demand reliable, high-quality tools and equipment.
  </p>`,
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.mail,
        techStack: [
        ]
      },
      {
        key: 'hp-product',
        name: 'HP',
        metadata: {
          title: "HP Lubricants – Industrial Lubricants",
          description: "HP Lubricants offers industrial and specialty oils with Alcade Engineers, trusted authorized distributor in Hyderabad, India.",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.analyticsServerTemplate.description:Hindustan Petroleum Corporation Limited (HPCL) manufactures and markets a
diverse selection of speciality products and lubricants under the umbrella brand HP
Lubricants. HP Lubricants provides a wide range of lubricants, specialities, and
greases, with over 450 grades available to serve a variety of industries, such as
automotive, industrial, mining, and agriculture. Their product line includes automotive
oils, industrial oils, greases, marine oils, and other speciality products that are
tailored to the changing requirements of a variety of industries.`,
        url: 'https://www.hplubricants.in/products/industrial-oils',
        logo: 'assets/logo/HPCL.svg',
        longDescription: `
        
  <p>
    Hindustan Petroleum Corporation Limited (HPCL) manufactures and markets a wide selection of specialty products and lubricants under the umbrella brand <strong>HP Lubricants</strong>. With over 450 grades available, HP Lubricants serves a broad range of industries including automotive, industrial, mining, agriculture, marine, and rail.
  </p>

  <p>
    The product line includes automotive oils, industrial lubricants, greases, marine oils, and other specialised products tailored to the evolving needs of each sector.
  </p>

  <h3>Product Categories</h3>

  <h4>1. Automotive Lubricants</h4>
  <ul>
    <li><strong>Engine Oils:</strong> Designed for passenger cars, commercial vehicles, and two-wheelers.</li>
    <li><strong>Gear Oils & Transmission Fluids:</strong> For smooth transmission performance and gear protection.</li>
    <li><strong>Automotive Greases:</strong> Suitable for off-highway equipment and general automotive use.</li>
  </ul>

  <h4>2. Industrial Lubricants</h4>
  <ul>
    <li><strong>Hydraulic Oils:</strong> For use in industrial and mobile hydraulic systems.</li>
    <li><strong>Metalworking Fluids:</strong> Engineered for cutting, grinding, and machining operations.</li>
    <li><strong>Compressor & Turbine Oils:</strong> For efficient operation of compressors and turbines.</li>
    <li><strong>Industrial Greases:</strong> High-performance greases for heavy machinery and equipment.</li>
  </ul>

  <h4>3. Specialty Lubricants</h4>
  <ul>
    <li><strong>Transformer Oils:</strong> High-dielectric strength oils for power equipment.</li>
    <li><strong>Textile Machinery Lubricants:</strong> Designed for precision textile equipment.</li>
    <li><strong>Rubber Process Oils:</strong> For rubber compounding and processing.</li>
    <li><strong>Heat Transfer Fluids:</strong> For closed and open-loop heating systems.</li>
  </ul>

  <h4>4. Marine and Rail Lubricants</h4>
  <ul>
    <li><strong>Marine Oils:</strong> High-performance oils for marine engines and vessels.</li>
    <li><strong>Railway Engine Lubricants:</strong> Greases and oils engineered for locomotives and rail systems.</li>
  </ul>

  <h4>5. Bio and Eco-Friendly Lubricants</h4>
  <ul>
    <li><strong>Eco-Safe Lubricants:</strong> Environmentally responsible lubricants for industries focused on sustainability.</li>
  </ul>

  <p>
    As India’s largest marketer of lubricants, <strong>HP Lubricants</strong> ensures performance, protection, and reliability across a wide range of applications. Their solutions are cost-effective, eco-friendly, and engineered for high performance.
  </p>

  <p>
    With a robust distribution network, expert technical support, and dedicated R&D, HP Lubricants is committed to delivering long-term value and innovation. Choosing HP Lubricants means prioritising quality, sustainability, and operational excellence.
  </p>`,
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.analytics,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'itw-product',
        name: 'ITW',
        metadata: {
          title: "ITW Products – Rust Preventives & Industrial Solutions",
          description: "Explore ITW’s metal repair, lubrication and rust preventive solutions with Alcade Engineers, trusted industrial distributors",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.smsServerTemplate.description:ITW Chemin, a business unit of ITW Inc., provides a diverse selection of products
and brands that are manufactured at its state-of-the-art facility. These products
include Devcon (Metal-filled Epoxies and Urethanes), LPS (Lubricants, Cleaners,
Degreasers), Plexus (Structural Adhesives), Accu-Lubecro lubrication systems,
Rustlick 631 (Instant Rust Fighter in Aerosol can), EC clean (Contact Cleaner),
Instant Rust Fighters, MRO Solutions, and Metal Repair Products, among others.`,
        url: 'https://www.itwchemin.com',
        logo: 'assets/logo/ITW.svg',
        longDescription: `
  <p>
    ITW Chemin, a business unit of ITW Inc., provides a diverse selection of high-performance industrial products and brands. These products are manufactured at its state-of-the-art facility and include:
  </p>

  <ul>
    <li><strong>Devcon:</strong> Metal-filled epoxies and urethanes for metal repair.</li>
    <li><strong>LPS:</strong> Lubricants, cleaners, and degreasers.</li>
    <li><strong>Plexus:</strong> Structural adhesives for high-strength bonding applications.</li>
    <li><strong>Accu-Lube:</strong> Advanced lubrication systems.</li>
    <li><strong>Rustlick 631:</strong> Instant rust fighter in aerosol form.</li>
    <li><strong>EC Clean:</strong> High-performance contact cleaner.</li>
    <li><strong>Instant Rust Fighters, MRO Solutions, Metal Repair Products</strong> and more.</li>
  </ul>

  <p>
    ITW Chemin focuses on high-performance industrial solutions, including a full spectrum of rust preventive products under the <strong>MET-L-GARD</strong> series. These products are engineered to protect metal surfaces from corrosion during manufacturing, storage, and transportation.
  </p>

  <h3>Main Rust Preventive Products</h3>

  <h4>1. MET-L-GARD RWD 403</h4>
  <ul>
    <li>
      Solvent-based, dewatering rust preventive oil designed for in-process protection of ferrous and alloy components.
    </li>
    <li>
      Forms a thin, greasy film that is easily removed with conventional industrial cleaners.
    </li>
    <li>
      Provides excellent protection, especially for consignments subject to sea shipment.
    </li>
  </ul>

  <h4>2. MET-L-GARD RP 631</h4>
  <ul>
    <li>
      Suitable for both ferrous and non-ferrous components.
    </li>
    <li>
      Forms an adherent soft oily film for long-lasting protection.
    </li>
    <li>
      Ideal for protecting metal parts during sea shipment.
    </li>
  </ul>

  <h4>3. MET-L-GARD RP 601CR</h4>
  <ul>
    <li>
      Designed specifically for the cold and hot rolled steel sectors.
    </li>
    <li>
      Can be applied via spray, felt roll, or brush.
    </li>
    <li>
      Leaves a protective film that can be removed using warm aqueous cleaners.
    </li>
  </ul>

  <p>
    These rust preventives are manufactured under stringent quality standards to meet the needs of multiple industrial sectors including <strong>steel, automotive, and textiles</strong>. Developed using <strong>eco-friendly materials</strong>, they ensure dependable corrosion resistance while promoting environmental responsibility.
  </p>`,
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        integrations: environment.sms,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'messer-product',
        name: 'Messer',
        metadata: {
          title: "Messer Systems",
          description: "Messer Systems Hyderabad",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.businessLogicServerTemplate.description:Messer is the world's largest privately held specialist in industrial, medical, and
specialty gases, operating across Europe, Asia, and the Americas. Messer is the
Cutting Systems partner of choice & Consultant for our customers. Messer's product
portfolio includes oxyfuel Gas Regulators, Cutting Torches, Cutting Nozzles &
Central Gas Distribution (gas manifold), flashback arrestors among other products.`,
        url: 'https://in.messer-cutting.com',
        logo: 'assets/logo/Messer.svg',
        longDescription: `
        <p>
    Messer is the world's largest privately held specialist in industrial, medical, and specialty gases, operating across Europe, Asia, and the Americas. As a trusted <strong>Cutting Systems partner and consultant</strong>, Messer offers a wide range of technologies and tools tailored for the metalworking industry.
  </p>

  <p>
    Messer's product portfolio includes:
  </p>

  <ul>
    <li>Oxyfuel Gas Regulators</li>
    <li>Cutting Torches</li>
    <li>Cutting Nozzles</li>
    <li>Central Gas Distribution Systems (gas manifold)</li>
    <li>Flashback Arrestors</li>
  </ul>

  <p>
    With cutting-edge tools and solutions, Messer Cutting Systems is a global leader in delivering innovative, high-performance equipment for a variety of metalworking applications.
  </p>

  <h3>Cutting Equipment</h3>

  <h4>1. ELEMENT 400L Unitised</h4>
  <ul>
    <li>Compact, all-in-one cutting machine combining laser beveling, plasma, and oxyfuel technologies.</li>
    <li>Designed for accuracy, adaptability, and small footprint operations.</li>
  </ul>

  <h4>2. FiberBlade V</h4>
  <ul>
    <li>High-speed fibre laser cutting machine built for complex and demanding tasks.</li>
    <li>Known for efficiency, precision, and reliability.</li>
  </ul>

  <h4>3. ELEMENT 400</h4>
  <ul>
    <li>Versatile machine supporting multiple cutting technologies.</li>
    <li>Customizable options to match specific production needs.</li>
  </ul>

  <h3>Methods of Cutting</h3>

  <h4>1. Oxyfuel Cutting</h4>
  <ul>
    <li>Best suited for heavy-duty cutting of thick steel plates.</li>
    <li>Uses a combination of oxygen and fuel gases.</li>
  </ul>

  <h4>2. Plasma Cutting</h4>
  <ul>
    <li>Accurate cutting of both ferrous and non-ferrous metals.</li>
    <li>Uses a high-velocity jet of ionized gas to melt and remove material.</li>
  </ul>

  <h4>3. Laser Cutting</h4>
  <ul>
    <li>Ideal for intricate designs and thin materials.</li>
    <li>Employs a focused laser beam for precise cuts and minimal waste.</li>
  </ul>

  <h3>Solutions for Material Handling</h3>
  <p>
    Messer offers <strong>integrated material handling systems</strong> designed to work seamlessly with their cutting machines. These systems enhance production efficiency by simplifying the loading and unloading process and reducing manual labor.
  </p>

  <p>
    With a combination of advanced equipment, adaptable cutting methods, smart software, and efficient material handling, <strong>Messer Cutting Systems</strong> delivers comprehensive solutions that meet the evolving needs of the global metalworking sector.
  </p>`,
        demonstrationImg: {
          src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
          description: 'Server That Manages Your Core Business Functionalities'
        },
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'taegutec-product',
        name: 'TaeguTec',
        metadata: {
          title: "TaeguTec Cutting Tools -  Precision Machining Solutions",
          description: "TaeguTec provides high-performance cutting tools and machining solutions through SR Engineers in Hyderabad, ensuring reliability",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.businessLogicServerTemplate.description:TaeguTec Ltd. is a multinational corporation with its headquarters in Daegu, South
Korea. It is the largest manufacturer of cutting tools in the Far East and the leading
producer of tungsten cutting tools and hard metal products, having been established
in 1952. The company's extensive product portfolio includes a diverse array of
metalworking solutions, such as Turning Tools, Parting & Grooving Tools, Thread
Making Tools, Hole Making Tools, Milling Tools, and Tooling Systems. These
products are designed to support a variety of industries, such as automotive, mould
and die, aerospace, and power generation, by offering contemporary machining
solutions that improve productivity and cost efficiency.`,
        url: 'https://www.taegutec.com/pages/hi',
        logo: 'assets/logo/TT.svg',
        longDescription: `<p>
    Headquartered in Daegu, South Korea, <strong>TaeguTec Ltd.</strong> is the largest manufacturer of cutting tools in the Far East and a leading global producer of tungsten cutting tools and hard metal products. Established in 1952, the company supports industries such as automotive, mould and die, aerospace, and power generation through contemporary machining solutions that boost productivity and reduce operational costs.
  </p>

  <p>
    <strong>TaeguTec India</strong> delivers a comprehensive range of cutting tools and tooling systems designed for a wide variety of machining applications.
  </p>

  <h3>Product Categories</h3>

  <h4>1. Milling Tools</h4>
  <ul>
    <li>
      <strong>Indexable Milling Cutters:</strong> Ideal for face milling, shoulder milling, and high-feed milling. These cutters use replaceable inserts to reduce downtime and enhance efficiency.
    </li>
    <li>
      <strong>Solid End Mills:</strong> Made from high-speed steel or solid carbide, these tools are engineered for precision milling with superior performance and durability.
    </li>
  </ul>

  <h4>2. Turning Tools</h4>
  <ul>
    <li>
      <strong>External Turning Tools:</strong> Used for machining outer surfaces, providing high precision and superior surface finish.
    </li>
    <li>
      <strong>Internal Turning Tools:</strong> Designed for boring and internal profiling, allowing access to complex internal geometries.
    </li>
    <li>
      <strong>Grooving & Parting Tools:</strong> Engineered for high-accuracy operations such as groove creation and parting off components.
    </li>
  </ul>

  <h4>3. Tools for Holemaking</h4>
  <ul>
    <li>
      <strong>Drills:</strong> Available in a variety of geometries, suitable for different materials and conditions to ensure optimal drilling performance.
    </li>
    <li>
      <strong>Reamers:</strong> Provide smooth surface finishes and precise hole dimensions for improved product quality.
    </li>
    <li>
      <strong>Boring Tools:</strong> Essential for enlarging holes with tight tolerances and exceptional accuracy.
    </li>
  </ul>

  <h4>4. Tools for Threading</h4>
  <ul>
    <li>
      <strong>Thread Mills:</strong> Create both internal and external threads with precision across various thread profiles.
    </li>
    <li>
      <strong>Taps:</strong> Used for cutting internal threads and offered in various thread standards and coatings for different materials.
    </li>
  </ul>

  <h4>5. Tooling Systems</h4>
  <ul>
    <li>
      <strong>Tool Holders:</strong> Ensure stable and precise clamping of cutting tools during machining operations.
    </li>
    <li>
      <strong>Collets:</strong> Provide high gripping force and concentricity, making them essential for accurate machining.
    </li>
    <li>
      <strong>Tool Holder Accessories:</strong> Include vital components like pull studs (retention knobs) that ensure secure engagement between the tool holder and the machine spindle.
    </li>
  </ul>`,
        demonstrationImg: {
          src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
          description: 'Server That Manages Your Core Business Functionalities'
        },
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      },
      {
        key: 'totem-product',
        name: 'TOTEM',
        metadata: {
          title: "TOTEM – Taps, Drills & Metal Cutting Solutions",
          description: "TOTEM delivers high-precision taps, drills & metal cutting solutions through SR Engineers in Hyderabad, ensuring trust",
          keywords: ["SR Group"]
        },
        description: $localize`:@@productLinesData.businessLogicServerTemplate.description:Under the brand TOTEM; Forbes Precision Tools and Machine Parts Limited
        produces round cutting tools made of high-speed steel and carbide. Currently, Totem
        has over 60 years of experience in the production of high-precision round cutting
        tools. Their services include internal and external threading, drilling, milling,
        deburring, and reaming. These tools are produced in a cutting-edge manufacturing
        facility located in Aurangabad, India. We are associated with TOTEM through its
        network of strong channel partners throughout India that service end customers in
        the automotive, defence, railway, aerospace, and engineering industries.`,
        url: 'https://forbesprecision.co.in',
        logo: 'assets/logo/Totem.jpg',
        longDescription: `

<p>TOTEM is the flagship brand of Forbes Precision Tools and Machine Parts Limited, a renowned division of Forbes &amp; Company. The company has its origins in 1968 under the illustrious Shapoorji Pallonji (SP) Group. TOTEM is headquartered in Aurangabad and Mumbai (Waluj &amp; Chikalthana), India, where it operates state-of-the-art manufacturing facilities. The company has more than 60 years of history.<br><br>TOTEM&apos;s precision, performance, and reliability are widely recognised as the result of its exceptional craftsmanship of high-speed steel (HSS) and carbide round cutting tools. TOTEM offers world-class capabilities to customers in the automotive, aerospace, defence, railways, oil &amp; gas, workshops, and heavy engineering sectors, powered by robust R&amp;D and CNC manufacturing technologies (including ANCA TapX Linear machines).</p>
<p><br></p>
<p><strong><u>Product Range</u></strong></p>
<p>TOTEM provides a wide selection of tooling solutions that are specifically designed for round cutting applications, such as:</p>
<p><br></p>
<p><strong>Threading</strong></p>
<p>1. HSS hand and machine taps in grades including M2, M35, and M42</p>
<p>2. Thread mills and carbide taps</p>
<p>3. External threading dies and die nuts (carbon steel and HSS)</p>
<p><br></p>
<p><strong>Drilling</strong></p>
<p>1. Solid carbide drills</p>
<p>2. HSS drills, such as silver and Deming, are available in both straight and taper shank formats.</p>
<p><br></p>
<p><strong>Reaming and Milling</strong></p>
<p>1. Solid carbide end mills (square and ball-nose)</p>
<p>2. Precision reamers for finishes with a tight tolerance.</p>
<p><br></p>
<p><strong>Rotary Cutting and Deburring</strong></p>
<p>1. Rotary burrs made of tungsten carbide that are designed for demanding applications, including stainless steels, hardened steels, and non-ferrous alloys.</p>
<p><br></p>
<p>TOTEM is a high-precision cutting tool brand that is distinguished by its engineering expertise, advanced manufacturing capabilities, and diverse product line. Every tool is designed to deliver industry-leading performance, durability, and excellence, from drilling and threading to milling, deburring, and reaming. TOTEM&apos;s tooling solutions are trusted by global industrial leaders and are built upon a legacy of innovation. They consistently meet the most stringent manufacturing standards of the present day.</p>`,
        demonstrationImg: {
          src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
          description: 'Server That Manages Your Core Business Functionalities'
        },
        openSource: false,
        features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
        pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
        techStack: [
          TECH_STACK.typeScript,
          TECH_STACK.node,
          TECH_STACK.express,
          TECH_STACK.mongoDb,
          TECH_STACK.heroku,
          TECH_STACK.npm
        ]
      }
    ],
  },
]

export const LIBRARIES: ProductLine[] = [
  {
    key: 'user-interface',
    name: 'User Interface Libraries',
    description: `Libraries for UI Templates on Open Template Hub`,
    products: [
      {
        key: 'animated-code-editor',
        name: 'Animated Code Editor',
        description: `This library contains animated code editor ui component for UI Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'animated-code-editor',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'animated-code-editor',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/generator/server-generator-logo.min.png',
        video: {
          url: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/demo/library/videos/animated-code-editor-video-1.mov',
          description: `Animated Code Editor demo`,
        },
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'led',
        name: 'Led',
        description: `This library contains led ui component for UI Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'led',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'led',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'card',
        name: 'Card',
        description: `This library contains card ui component for UI Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'card',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'card',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'button',
        name: 'Button',
        description: `This library contains button ui component for UI Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'button',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'button',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'hero',
        name: 'Hero',
        description: `This library contains hero ui component for UI Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'hero',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'hero',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'ui-library-template',
        name: 'UI Library Template',
        description: `This library is a template to generate new angular component libraries.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'ui-library-template',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'ui-library-template',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
    ],
  },
  {
    key: 'server',
    name: 'Other Libraries',
    description: `Libraries for Server Templates on Open Template Hub`,
    products: [
      {
        key: 'swagger-decorators',
        name: 'Swagger Decorators',
        description: `OTH library that contains methods and decorators to ease and automate Swagger documentation.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'swagger-decorators',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'swagger-decorators',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/library/swagger-decorators-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
      {
        key: 'common',
        name: 'Common',
        description: `This library contains common functionalities for Server Templates on Open Template Hub`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'common',
        urlAlter: environmentCommon.website.npm.url + '/package/' + environmentCommon.company.social.npm + '/' + 'common',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        openSource: false,
        hasNpmPackage: false,
        redirectToUrl: false
      },
    ],
  },
];

export const PLUGINS: ProductLine[] = [
  {
    key: 'plugins',
    name: 'Plugins',
    description: `Plugins made by Open Template Hub`,
    products: [
      {
        key: 'oth-intellij-theme-plugin',
        name: 'IntelliJ Theme',
        description: `OTH themes for JetBrains. Dark and light themes using Open Template Hub's color palette.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github + '/' + 'oth-intellij-theme-plugin',
        urlAlter: 'https://plugins.jetbrains.com/plugin/18932-oth-theme',
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/third-parties/logo/intellij.png',
        openSource: false,
        redirectToUrlAlter: false
      },
    ],
  }
];

export const SERVICES: ProductLine[] = [
  {
    key: 'services',
    name: '',
    description: 'Please check our awards',
    products: [
      {
        key: 'software-consultancy',
        name: 'Software Consultancy',
        description: $localize`:@@servicesData.softwareConsultancy.description:We perform world-class custom software development services from startups to enterprise businesses. Our highly experienced software developers have a deep understanding of how to leverage top programming languages, frameworks, and other software development tools to create the ideal solution for your business.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github,
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo.min.png',
        features: [{
          name: $localize`:@@servicesData.softwareConsultancy.features.name:Features`,
          details: [
            {
              name: $localize`:@@servicesData.softwareConsultancy.features.details.1.name:Custom Application Development`,
              description: $localize`:@@servicesData.softwareConsultancy.features.details.1.description:We rely on our industry-specific technology experience to deliver highly scalable, flexible, and interoperable web, mobile, desktop, and hybrid applications.`
            },
            {
              name: $localize`:@@servicesData.softwareConsultancy.features.details.2.name:Application Maintenance`,
              description: $localize`:@@servicesData.softwareConsultancy.features.details.2.description:Our app maintenance & modernization services are designed to ensure the scalability, performance, and sustainability of your entire software infrastructure.`
            },
            {
              name: $localize`:@@servicesData.softwareConsultancy.features.details.3.name:Implementation & Deployment`,
              description: $localize`:@@servicesData.softwareConsultancy.features.details.3.description:We devise an in-depth, comprehensive software implementation & deployment plan, assessing your needs to deliver enhanced technologies to end-users.`
            },
          ],
        }
        ],
      },
      {
        key: 'software-integration',
        name: 'Software Integration',
        description: $localize`:@@servicesData.softwareIntegration.description:When a company adopts a new technology or business process, they face many challenges between their current applications and systems and the complicated software implementation process. Our industry-specific software engineers handle all of your challenging integration & implementation obstacles, including architectural design, testing, debugging, and execution.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github,
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo-pieces.min.png',
        features: [{
          name: $localize`:@@servicesData.softwareIntegration.features.name:Features`,
          details: [
            {
              name: $localize`:@@servicesData.softwareIntegration.features.details.1.name:Data Integration Services`,
              description: $localize`:@@servicesData.softwareIntegration.features.details.1.description:We perform data integration services, including merging data, consolidating business processes, and creating Database Management Systems to ensure complete data integrity during the transfer process.`
            },
            {
              name: $localize`:@@servicesData.softwareIntegration.features.details.2.name:API Integration Services`,
              description: $localize`:@@servicesData.softwareIntegration.features.details.2.description:We provide integrating custom-built and third-party APIs service. We, integrate, and customize add web service functionality to mobile and web applications, and seamlessly synchronize data formats across these applications.`
            },
            {
              name: $localize`:@@servicesData.softwareIntegration.features.details.3.name:Enterprise Application Integrations`,
              description: $localize`:@@servicesData.softwareIntegration.features.details.3.description:We provide Enterprise Application Integration solutions, facilitating seamless communications between business platforms. We are providing integrations for content management, accounting, customer relationship management, analytics, and marketing.`
            },
          ],
        }
        ],
      },
      {
        key: 'quality-assurance',
        name: 'Quality Assurance',
        description: $localize`:@@servicesData.qualityAssurance.description:We deliver full-cycle QA automated software testing for web, mobile, and desktop applications to enable improved test coverage, enhance product quality, optimize testing activities, boost productivity, and decrease overall testing times.`,
        url: environmentCommon.website.github.url + '/' + environmentCommon.company.social.github,
        logo: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/logo/brand-logo-shine.min.png',
        features: [{
          name: $localize`:@@servicesData.qualityAssurance.features.name:Features`,
          details: [
            {
              name: $localize`:@@servicesData.qualityAssurance.features.details.1.name:Software QA Automation Testing`,
              description: $localize`:@@servicesData.qualityAssurance.features.details.1.description:We deliver full-cycle QA automated software testing for web, mobile, and desktop applications to enable improved test coverage, enhance product quality, optimize testing activities, boost productivity, and decrease overall testing times.`
            },
            {
              name: $localize`:@@servicesData.qualityAssurance.features.details.2.name:Software QA Manual Testing`,
              description: $localize`:@@servicesData.qualityAssurance.features.details.2.description:Our dedicated QA team will test your mobile, web, or desktop application manually to identify & fix bugs, detect & eradicate errors, and confirm its compliance with regulatory standards, providing you with the highest quality result possible.`
            },
            {
              name: $localize`:@@servicesData.qualityAssurance.features.details.3.name:Software QA Usability Testing`,
              description: $localize`:@@servicesData.qualityAssurance.features.details.3.description:We design QA usability testing platforms that incorporate specific usability testing tools and UX research methods selected based on the client, focused on measuring how user-friendly and flexible your application or website is.`
            },
          ],
        }
        ]
      },
    ],
  },
];


export const COMPANIES: CompanyLine[] = [
  {
    key: 1,
    name: 'Alcade Engineers Pvt. Ltd.',
    description: '',
    products:
      [
        {
          key: 'product',
          name: '',
          description: "Please go through our companies",
          products: [
            {
              key: 'ewac-product',
              name: 'EWAC',
              metadata: {
                title: "EWAC Products – Welding, Maintenance and Repair",
                description: "Alcade Engineers offers EWAC products - premium alloys, electrodes and solutions for industrial maintenance and repair"
              },
              description: $localize`:@@productLinesData.fileStorageTemplate.description:Established in 1962 as a joint venture between Larsen &amp; Toubro Limited, India, and
  Eutectic Castolin Group, Switzerland, EWAC Alloys Limited specialises in
  maintenance and repair solutions within the welding industry. Their product lineup
  includes specialised welding alloys, electrodes, gas brazing products, soldering
  alloys, thermal spray powders, metal and flux-cored continuous wires, polymer-
  based compounds, wear-resistant liners, ceramic products, and welding equipment.
  The company serves sectors such as steel, cement, power, sugar, mining, oil and
  gas refineries, tyre, port, and road transport industries.`,
              url: 'https://www.ewacalloys.com',
              logo: 'assets/logo/Ewac.svg',
              openSource: false,
              features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
              pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
              integrations: environment.fileStorage,
              techStack: [
                TECH_STACK.typeScript,
                TECH_STACK.node,
                TECH_STACK.express,
                TECH_STACK.postgreSql,
                TECH_STACK.mongoDb,
                TECH_STACK.heroku,
                TECH_STACK.npm
              ]
            },
            {
              key: 'groz-product',
              name: 'Groz',
              metadata: {
                title: "GROZ– Tools, Maintenance & construction equipment",
                description: "Explore GROZ precision tools, lubrication and maintenance equipment with Alcade Engineers, trusted industrial distributor in Hyderabad"
              },
              description: $localize`:@@productLinesData.mailServerTemplate.description:Groz Engineering Tools is a leading global manufacturer specialising in lubrication
  equipment, precision engineering, professional hand tools, and automotive shop
  equipment. Groz serves industries such as automotive, industrial, agriculture,
  mining, and DIY, delivering engineering solutions that emphasize precision and
  durability. High quality products include Lubrication Equipment such as Grease guns,
  oil pumps, and fluid handling solutions for industrial and automotive applications;
  Precision Engineering Tools such as High-performance measuring instruments,
  cutting tools, and workshop essentials; Hand Tools such as Wrenches, pliers,
  hammers, and other professional-grade tools for various industries; Automotive &amp;
  
  Industrial Equipment such as Shop presses, work lights, fuel transfer pumps, and
  accessories for maintenance and repair.`,
              url: 'https://groz-tools.com',
              logo: 'assets/logo/GROZ.svg',
              openSource: false,
              features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
              pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
              integrations: environment.mail,
              techStack: [
              ]
            },
            {
              key: 'hp-product',
              name: 'HP',
              metadata: {
                title: "HP Lubricants – Industrial Lubricants",
                description: "HP Lubricants offers industrial and specialty oils with Alcade Engineers, trusted authorized distributor in Hyderabad, India."
              },
              description: $localize`:@@productLinesData.analyticsServerTemplate.description:Hindustan Petroleum Corporation Limited (HPCL) manufactures and markets a
  diverse selection of speciality products and lubricants under the umbrella brand HP
  Lubricants. HP Lubricants provides a wide range of lubricants, specialities, and
  greases, with over 450 grades available to serve a variety of industries, such as
  automotive, industrial, mining, and agriculture. Their product line includes automotive
  oils, industrial oils, greases, marine oils, and other speciality products that are
  tailored to the changing requirements of a variety of industries.`,
              url: 'https://www.hplubricants.in/products/industrial-oils',
              logo: 'assets/logo/HPCL.svg',
              openSource: false,
              features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
              pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
              integrations: environment.analytics,
              techStack: [
                TECH_STACK.typeScript,
                TECH_STACK.node,
                TECH_STACK.express,
                TECH_STACK.mongoDb,
                TECH_STACK.heroku,
                TECH_STACK.npm
              ]
            },
            {
              key: 'itw-product',
              name: 'ITW',
              metadata: {
                title: "ITW Products – Rust Preventives & Industrial Solutions",
                description: "Explore ITW’s metal repair, lubrication and rust preventive solutions with Alcade Engineers, trusted industrial distributors"
              },
              description: $localize`:@@productLinesData.smsServerTemplate.description:ITW Chemin, a business unit of ITW Inc., provides a diverse selection of products
  and brands that are manufactured at its state-of-the-art facility. These products
  include Devcon (Metal-filled Epoxies and Urethanes), LPS (Lubricants, Cleaners,
  Degreasers), Plexus (Structural Adhesives), Accu-Lubecro lubrication systems,
  Rustlick 631 (Instant Rust Fighter in Aerosol can), EC clean (Contact Cleaner),
  Instant Rust Fighters, MRO Solutions, and Metal Repair Products, among others.`,
              url: 'https://www.itwchemin.com',
              logo: 'assets/logo/ITW.svg',
              openSource: false,
              features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
              pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
              integrations: environment.sms,
              techStack: [
                TECH_STACK.typeScript,
                TECH_STACK.node,
                TECH_STACK.express,
                TECH_STACK.heroku,
                TECH_STACK.npm
              ]
            },
            {
              key: 'messer-product',
              name: 'Messer',
              metadata: {
                title: "Messer Systems",
                description: ""
              },
              description: $localize`:@@productLinesData.businessLogicServerTemplate.description:Messer is the world&#39;s largest privately held specialist in industrial, medical, and
  specialty gases, operating across Europe, Asia, and the Americas. Messer is the
  Cutting Systems partner of choice &amp; Consultant for our customers. Messer&#39;s product
  portfolio includes oxyfuel Gas Regulators, Cutting Torches, Cutting Nozzles &amp;
  Central Gas Distribution (gas manifold), flashback arrestors among other products.`,
              url: 'https://in.messer-cutting.com',
              logo: 'assets/logo/Messer.svg',
              demonstrationImg: {
                src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
                description: 'Server That Manages Your Core Business Functionalities'
              },
              openSource: false,
              features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
              pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
              techStack: [
                TECH_STACK.typeScript,
                TECH_STACK.node,
                TECH_STACK.express,
                TECH_STACK.mongoDb,
                TECH_STACK.heroku,
                TECH_STACK.npm
              ]
            },
          ]
        },
      ],
  },
  {
    key: 2,
    name: 'SR Engineers',
    description: '',
    products: [
      {
        key: 'product',
        name: '',
        description: "",
        products: [
          {
            key: 'bahco-product',
            name: 'Bahco',
            metadata: {
              title: "Bacho Blades - Precision cutting blades & tools",
              description: "Explore Bahco professional hand tools blades & saws with SR Group, trusted distributor for industrial and engineering solutions."
            },
            description: $localize`:@@productLinesData.paymentServerTemplate.description:Bahco designs and manufactures tools and saws for the most demanding
    professionals, prioritising innovation, performance, and ergonomics. Bacho have
    been inventors for over 150 years, introducing the most innovative tools in the world
    and selling through distributors of which we are one and we mutually form long-term
    partnerships focusing on customer relation and satisfaction. Products: Bahco band
    saw, the Holesaw hacksaw, the Jig saw blade, the Hand saw blade, the Power Hack
    Saw blade, the Hole Hacksaw, the Hole Saw set, and the Reciprocating Saw.`,
            url: 'https://www.bahco.com/int_en/products/bandsaws-power-saws/metal-cutting-carbide-bandsaws.html',
            logo: 'assets/logo/Bahco.svg',
            openSource: false,
            features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
            pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
            integrations: environment.payment,
            techStack: [
            ]
          },
          {
            key: 'castrol-product',
            name: 'Castrol',
            metadata: {
              title: "Castrol Industrial Lubricants – Performance & Trust",
              description: "Castrol’s high-performance industrial lubricants, distributed by SR Engineers, ensure superior efficiency & reliability in India"
            },
            description: $localize`:@@productLinesData.authServerTemplate.description:Castrol, a global pioneer in lubricating solutions, has been at the cutting edge of
    innovation for over a century. Castrol's high-performance lubricants serve the
    automotive, aerospace, manufacturing, and energy sectors. Castrol supplies cutting-
    edge lubrication technology that enhances sustainability, performance, and
    efficiency.Castrol manufactures neat and soluble cutting oils, rust and corrosion
    preventives, mineral and FRHF hydraulic oils, engine oils, gear oils, transformer oils,
    synthetic oils (poly glycol, poly alfa olefines), speciality greases for heavy duty and
    high temperatures, gel-based girth gear lubricants and other speciality lubricant
    products.`,
            url: 'https://www.castrol.com/en_in/india/home/industries.html',
            logo: 'assets/logo/Castrol.svg',
            openSource: false,
            features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
            pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
            integrations: environment.oauth,
            techStack: [
            ]
          },
          {
            key: 'taegutec-product',
            name: 'TaeguTec',
            metadata: {
              title: "TaeguTec Cutting Tools -  Precision Machining Solutions",
              description: "TaeguTec provides high-performance cutting tools and machining solutions through SR Engineers in Hyderabad, ensuring reliability"
            },
            description: $localize`:@@productLinesData.businessLogicServerTemplate.description:TaeguTec Ltd. is a multinational corporation with its headquarters in Daegu, South
    Korea. It is the largest manufacturer of cutting tools in the Far East and the leading
    producer of tungsten cutting tools and hard metal products, having been established
    in 1952. The company&#39;s extensive product portfolio includes a diverse array of
    metalworking solutions, such as Turning Tools, Parting &amp; Grooving Tools, Thread
    Making Tools, Hole Making Tools, Milling Tools, and Tooling Systems. These
    products are designed to support a variety of industries, such as automotive, mould
    and die, aerospace, and power generation, by offering contemporary machining
    solutions that improve productivity and cost efficiency.`,
            url: 'https://www.taegutec.com/pages/hi',
            logo: 'assets/logo/TT.svg',
            demonstrationImg: {
              src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
              description: 'Server That Manages Your Core Business Functionalities'
            },
            openSource: false,
            features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
            pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
            techStack: [
              TECH_STACK.typeScript,
              TECH_STACK.node,
              TECH_STACK.express,
              TECH_STACK.mongoDb,
              TECH_STACK.heroku,
              TECH_STACK.npm
            ]
          },
          {
            key: 'totem-product',
            name: 'TOTEM',
            metadata: {
              title: "TOTEM – Taps, Drills & Metal Cutting Solutions",
              description: "TOTEM delivers high-precision taps, drills & metal cutting solutions through SR Engineers in Hyderabad, ensuring trust"
            },
            description: $localize`:@@productLinesData.businessLogicServerTemplate.description:Under the brand TOTEM; Forbes Precision Tools and Machine Parts Limited
            produces round cutting tools made of high-speed steel and carbide. Currently, Totem
            has over 60 years of experience in the production of high-precision round cutting
            tools. Their services include internal and external threading, drilling, milling,
            deburring, and reaming. These tools are produced in a cutting-edge manufacturing
            facility located in Aurangabad, India. We are associated with TOTEM through its
            network of strong channel partners throughout India that service end customers in
            the automotive, defence, railway, aerospace, and engineering industries.`,
            url: 'https://forbesprecision.co.in',
            logo: 'assets/logo/Totem.jpg',
            demonstrationImg: {
              src: 'https://raw.githubusercontent.com/open-template-hub/open-template-hub.github.io/master/assets/min/demo/server/business-logic-server-demo.min.png',
              description: 'Server That Manages Your Core Business Functionalities'
            },
            openSource: false,
            features: DEFAULT_OPEN_SOURCE_SERVER_FEATURES,
            pricingOptions: DEFAULT_OPEN_SOURCE_SERVER_PRICING_OPTIONS,
            techStack: [
              TECH_STACK.typeScript,
              TECH_STACK.node,
              TECH_STACK.express,
              TECH_STACK.mongoDb,
              TECH_STACK.heroku,
              TECH_STACK.npm
            ]
          },
        ]
      },
    ]
  },
  {
    key: 3,
    name: 'SR Logistics India Pvt. Ltd.',
    description: '',
    products: []
  }
]